import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { googleMapsAPI, hiringLinksAPI, type HiringRaiseCity } from '../../services/api';
import { GooglePlacesSession } from '../../utils/googlePlacesSession';
import { usePanelPosition } from './HiringSelect';
import type { PickedCity } from './HiringPickers';
import {
  RAISE_FORM,
  TYPED_ADDRESS_MAX,
  addressCityCandidates,
  exactCityOf,
  mapPointOf,
  placePincode,
  streetOfPickedAddress,
  type HiringMapPoint,
} from './hiringText';

/**
 * The typed address of a raise with no store, with Google's address suggestions - the same
 * ones Add Store offers on the Stores page (added 15 Oct 2026).
 *
 * It uses what Add Store uses, and nothing new: `googleMapsAPI.autocomplete` and
 * `placeDetails` (the server's /api/google-maps routes, which need sign-in, are rate-limited
 * and hold the key), and `GooglePlacesSession`, with Add Store's rules: one token for a
 * typing session, completed when a suggestion is picked, reset when the box is cleared;
 * nothing is asked until 3 characters are typed, and then only after 500 ms without typing.
 *
 * Picking a suggestion fills the address and, from the place's details, the 6-digit PIN code
 * and the map point; the city is chosen from Recruitment's own list, and is picked here only
 * when exactly one of its cities has the address's locality as its name. The Address box
 * keeps only the street part of what Google wrote (streetOfPickedAddress): the city, the
 * state, the PIN code and "India" at its end are the window's own City and PIN, and are not
 * said - or sent - twice. Typing without
 * picking is allowed as before: a plain address, no map point. With no key on the server or
 * any error from Google, the box stays a plain text box, with a quiet "Suggestions
 * unavailable" under it and nothing else.
 */

const SEARCH_AFTER_MS = 500;
const SEARCH_FROM = 3;

interface Suggestion { address: string; placeId: string }

/** What a pick fills. Each field is set only when the pick has it. */
export interface AddressPick {
  address: string;
  pincode?: string;
  /** The picked place's map point; null while its details are read, or when they had none. */
  point?: HiringMapPoint | null;
  city?: PickedCity;
}

const cancelled = (err: unknown, signal: AbortSignal) =>
  signal.aborted || (typeof err === 'object' && err !== null && 'code' in err && (err as { code?: unknown }).code === 'ERR_CANCELED');

export function HiringAddressInput({
  id, projectId, asking, value, city, onType, onPick, disabled = false, className,
}: {
  id: string;
  projectId: number;
  asking: boolean;
  value: string;
  /** The city picked so far: the suggestions are looked for there, as Add Store does. */
  city: PickedCity | null;
  /** The person typed: the address is plain text again, with no map point. */
  onType: (text: string) => void;
  onPick: (pick: AddressPick) => void;
  disabled?: boolean;
  className: string;
}) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const session = useRef(new GooglePlacesSession());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbort = useRef<AbortController | null>(null);
  const detailsAbort = useRef<AbortController | null>(null);
  const sequence = useRef(0);
  // The token the suggestions on screen were asked with: the pick completes that one.
  const suggestionsToken = useRef<string | null>(null);
  const owner = useRef({ onPick, projectId, asking });
  owner.current = { onPick, projectId, asking };
  const pos = usePanelPosition(open && suggestions.length > 0, inputRef, 0);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    searchAbort.current?.abort();
    detailsAbort.current?.abort();
    session.current.reset();
  }, []);

  const stopSearching = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    searchAbort.current?.abort();
    searchAbort.current = null;
    sequence.current += 1;
    setLoading(false);
    setSuggestions([]);
    setOpen(false);
  };

  const search = async (text: string) => {
    const input = text.trim();
    if (input.length < SEARCH_FROM) return;
    searchAbort.current?.abort();
    const control = new AbortController();
    searchAbort.current = control;
    const n = ++sequence.current;
    const sessionToken = session.current.getToken();
    setLoading(true);
    try {
      const res = await googleMapsAPI.autocomplete({
        input,
        city: city?.label || undefined,
        state: city?.stateName || undefined,
        sessionToken,
      }, control.signal);
      if (control.signal.aborted || n !== sequence.current) return;
      if (res?.data?.status !== 'success') {
        suggestionsToken.current = null;
        setSuggestions([]);
        setOpen(false);
        setUnavailable(true);
        return;
      }
      const found: Suggestion[] = (Array.isArray(res.data.data) ? res.data.data : [])
        .map((item: { address?: unknown; placeId?: unknown }) => ({ address: String(item?.address ?? ''), placeId: String(item?.placeId ?? '') }))
        .filter((item: Suggestion) => item.address);
      suggestionsToken.current = sessionToken;
      setUnavailable(false);
      setSuggestions(found);
      setActive(0);
      setOpen(found.length > 0);
    } catch (err: unknown) {
      if (cancelled(err, control.signal) || n !== sequence.current) return;
      // No key on the server, Google refusing, the rate limit: a plain box, said quietly.
      suggestionsToken.current = null;
      setSuggestions([]);
      setOpen(false);
      setUnavailable(true);
    } finally {
      if (n === sequence.current) {
        searchAbort.current = null;
        setLoading(false);
      }
    }
  };

  const type = (text: string) => {
    onType(text);
    stopSearching();
    detailsAbort.current?.abort();
    detailsAbort.current = null;
    suggestionsToken.current = null;
    if (text.trim().length < SEARCH_FROM) {
      if (!text.trim()) session.current.reset();
      return;
    }
    timer.current = setTimeout(() => { void search(text); }, SEARCH_AFTER_MS);
  };

  /**
   * The city of Recruitment's list with the address's locality as its exact name, if there is
   * one. It is read from Google's whole address; the box then drops that city's name too.
   */
  const findCity = async (address: string, named: { locality?: unknown; district?: unknown }, n: number) => {
    for (const name of addressCityCandidates(address, named)) {
      try {
        const { projectId: project, asking: ask } = owner.current;
        const res = await (ask ? hiringLinksAPI.requestCities(project, name) : hiringLinksAPI.raiseCities(project, name));
        if (n !== sequence.current) return;
        const hit = exactCityOf<HiringRaiseCity>(res?.data?.data?.cities ?? [], name, address);
        if (hit) {
          owner.current.onPick({
            address: streetOfPickedAddress(address, { ...named, city: hit.cityName, state: hit.stateName }).slice(0, TYPED_ADDRESS_MAX),
            city: { cityId: hit.cityId, label: hit.cityName, stateName: hit.stateName },
          });
          return;
        }
      } catch {
        // The city is the person's to pick, as it always was.
        return;
      }
    }
  };

  const pick = async (suggestion: Suggestion) => {
    stopSearching();
    const n = sequence.current;
    // The city picked so far, as the suggestions were looked for there.
    const picked = { city: city?.label, state: city?.stateName };
    onPick({ address: streetOfPickedAddress(suggestion.address, picked).slice(0, TYPED_ADDRESS_MAX), point: null });
    if (!suggestion.placeId) {
      suggestionsToken.current = null;
      session.current.reset();
      return;
    }
    const sessionToken = suggestionsToken.current || session.current.getToken();
    suggestionsToken.current = null;
    session.current.complete(sessionToken);
    detailsAbort.current?.abort();
    const control = new AbortController();
    detailsAbort.current = control;
    try {
      const res = await googleMapsAPI.placeDetails(suggestion.placeId, sessionToken, control.signal);
      if (control.signal.aborted || n !== sequence.current || res?.data?.status !== 'success') return;
      const details = res.data.data ?? {};
      const whole = String(details.address || suggestion.address).trim();
      const named = { locality: details.locality, district: details.district };
      const pincode = placePincode(details.pincode);
      owner.current.onPick({
        address: streetOfPickedAddress(whole, { ...named, ...picked }).slice(0, TYPED_ADDRESS_MAX),
        ...(pincode ? { pincode } : {}),
        point: mapPointOf({ lat: details.latitude, lng: details.longitude }),
      });
      await findCity(whole, named, n);
    } catch (err: unknown) {
      if (cancelled(err, control.signal)) return;
      // The address stays as picked, with no map point: the city's point is used, as for a typed one.
    } finally {
      if (detailsAbort.current === control) detailsAbort.current = null;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = suggestions[active];
      if (hit) void pick(hit);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const shown = open && suggestions.length > 0 && pos;
  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={!!shown}
          aria-controls={shown ? listId : undefined}
          aria-activedescendant={shown ? `${listId}-${active}` : undefined}
          autoComplete="off"
          value={value}
          maxLength={TYPED_ADDRESS_MAX}
          placeholder={RAISE_FORM.addressPlaceholder}
          onChange={(e) => type(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onBlur={() => setOpen(false)}
          disabled={disabled}
          className={className}
        />
        {loading && (
          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />
          </span>
        )}
      </div>
      {unavailable && <p className="mt-1 text-xs text-gray-400">{RAISE_FORM.suggestionsUnavailable}</p>}
      {shown && createPortal(
        <div
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 140 }}
          className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          <div id={listId} role="listbox" aria-label={RAISE_FORM.addressSuggestions} className="max-h-60 overflow-y-auto py-1">
            {suggestions.map((suggestion, i) => (
              <div
                key={suggestion.placeId || i}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                // Picked on mouse-down, so the box keeps its focus and does not close the list first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  void pick(suggestion);
                }}
                onMouseEnter={() => setActive(i)}
                title={suggestion.address}
                className={`cursor-pointer truncate px-3 py-2 text-[13px] text-gray-900 ${i === active ? 'bg-gray-100' : ''}`}
              >
                {suggestion.address}
              </div>
            ))}
          </div>
          <div className="flex justify-end border-t border-gray-100 px-2.5 pb-[5px] pt-2">
            <img src="/google-maps-logo.svg" alt="Google Maps" className="h-[18px] w-[98px]" />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default HiringAddressInput;
